/**
 * NotificationsBell — the header-bar inbox.
 *
 * Self-contained: a compact bell button with an unread badge
 * (GET /api/v1/notifications/unread-count, polled every 60s) opening a Drawer
 * that lists the caller's own deliveries (GET /api/v1/notifications). A click
 * on an unread row marks THAT delivery read (POST /:id/read); the footer marks
 * everything (POST /read-all). Holders of `notification.send` also get the
 * fan-out form (POST /notifications).
 *
 * Renders NOTHING for users without `notification.view.self` — the mount point
 * does not need its own permission check. It makes no assumption about where
 * it is mounted; any header bar can drop it in.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Drawer,
  FormField,
  Input,
  Modal,
  Select,
  Textarea,
  useToast,
} from '@sonauto/ui';
import { errorMessage } from '../lib/api.js';
import {
  NOTIFICATION_AUDIENCE_TYPES,
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_PRIORITIES,
  notificationsApi,
} from '../lib/adminApi/notifications.js';
import type {
  NotificationAudienceType,
  NotificationCategory,
  NotificationItem,
  NotificationPriority,
} from '../lib/adminApi/notifications.js';
import { useAuth } from '../auth/AuthProvider.js';
import { QueryBoundary } from './QueryBoundary.js';

export function NotificationsBell(): React.JSX.Element | null {
  const { has } = useAuth();
  const [open, setOpen] = useState(false);
  const canView = has('notification.view.self');

  const unreadQuery = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsApi.unreadCount(),
    enabled: canView,
    refetchInterval: 60_000,
  });

  if (!canView) return null;

  const unread = unreadQuery.data?.unread ?? 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={unread > 0 ? `Thông báo (${unread} chưa đọc)` : 'Thông báo'}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-lg text-fg-muted transition-colors hover:bg-surface-sunken hover:text-fg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-solid"
      >
        <span aria-hidden="true">🔔</span>
        {unread > 0 ? (
          <Badge
            tone="danger"
            variant="solid"
            size="sm"
            className="absolute -right-1.5 -top-1 min-w-[1.25rem] justify-center px-1"
          >
            {unread > 99 ? '99+' : unread}
          </Badge>
        ) : null}
      </button>

      {open ? <NotificationsDrawer onClose={() => setOpen(false)} /> : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// The Drawer — list + mark-read + read-all + (optional) send
// ---------------------------------------------------------------------------

function NotificationsDrawer({ onClose }: { readonly onClose: () => void }): React.JSX.Element {
  const { has } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [sending, setSending] = useState(false);

  const listQuery = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => notificationsApi.list({ limit: 50 }),
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const markRead = useMutation({
    mutationFn: (deliveryId: string) => notificationsApi.markRead(deliveryId),
    onSuccess: invalidate,
    onError: (e: unknown) => toast.error('Không đánh dấu được', errorMessage(e)),
  });

  const readAll = useMutation({
    mutationFn: () => notificationsApi.readAll(),
    onSuccess: async (result) => {
      await invalidate();
      toast.success('Đã đánh dấu tất cả đã đọc', `${result.marked} thông báo được cập nhật.`);
    },
    onError: (e: unknown) => toast.error('Không đánh dấu được', errorMessage(e)),
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title="Thông báo"
      description="Thông báo chưa đọc hiển thị trước. Nhấn vào một dòng để đánh dấu đã đọc."
      footer={
        <>
          {has('notification.send') ? (
            <Button variant="secondary" size="sm" onClick={() => setSending(true)}>
              Gửi thông báo
            </Button>
          ) : null}
          <Button
            size="sm"
            loading={readAll.isPending}
            onClick={() => readAll.mutateAsync().catch(() => undefined)}
          >
            Đánh dấu tất cả đã đọc
          </Button>
        </>
      }
    >
      <QueryBoundary
        isLoading={listQuery.isLoading}
        isError={listQuery.isError}
        error={listQuery.error}
        data={listQuery.data}
        onRetry={() => void listQuery.refetch()}
        isEmpty={(d) => d.notifications.length === 0}
        emptyTitle="Không có thông báo nào"
        emptyDescription="Thông báo hệ thống và tin từ quản lý sẽ xuất hiện tại đây."
      >
        {(data) => (
          <ul className="-mx-1 flex flex-col gap-1">
            {data.notifications.map((item) => (
              <NotificationRow
                key={item.deliveryId}
                item={item}
                onMarkRead={() => markRead.mutate(item.deliveryId)}
              />
            ))}
          </ul>
        )}
      </QueryBoundary>

      {sending ? <SendNotificationDialog onClose={() => setSending(false)} /> : null}
    </Drawer>
  );
}

function NotificationRow({
  item,
  onMarkRead,
}: {
  readonly item: NotificationItem;
  readonly onMarkRead: () => void;
}): React.JSX.Element {
  const unread = item.readAt === null;
  return (
    <li>
      <button
        type="button"
        onClick={unread ? onMarkRead : undefined}
        disabled={!unread}
        className={`w-full rounded-md px-3 py-2.5 text-left transition-colors ${
          unread ? 'bg-info-bg/50 hover:bg-info-bg' : 'opacity-75'
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <p className={`min-w-0 truncate text-sm text-fg ${unread ? 'font-semibold' : 'font-medium'}`}>
            {unread ? (
              <span aria-hidden="true" className="mr-1.5 inline-block h-2 w-2 rounded-full bg-info-solid align-middle" />
            ) : null}
            {item.title}
          </p>
          {item.priority === 'HIGH' || item.priority === 'URGENT' ? (
            <Badge tone={item.priority === 'URGENT' ? 'danger' : 'warning'} size="sm">
              {item.priority === 'URGENT' ? 'Khẩn' : 'Ưu tiên'}
            </Badge>
          ) : null}
        </div>
        {item.body !== null && item.body !== '' ? (
          <p className="mt-0.5 line-clamp-2 text-xs text-fg-muted">{item.body}</p>
        ) : null}
        <p className="mt-1 text-[11px] text-fg-subtle">
          {new Date(item.createdAt).toLocaleString('vi-VN')}
          {unread ? ' · chưa đọc' : ''}
        </p>
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Send dialog — POST /notifications (permission `notification.send`)
// ---------------------------------------------------------------------------

const AUDIENCE_LABEL_VI: Readonly<Record<NotificationAudienceType, string>> = {
  USER: 'Một người dùng',
  ROLE: 'Theo vai trò',
  BRANCH: 'Cả chi nhánh',
  STATION: 'Theo trạm',
};

const AUDIENCE_REF_HINT_VI: Readonly<Record<NotificationAudienceType, string>> = {
  USER: 'ID người dùng nhận thông báo.',
  ROLE: 'Mã vai trò (ví dụ BRANCH_MANAGER).',
  BRANCH: 'ID chi nhánh — mọi người dùng có vai trò tại chi nhánh sẽ nhận.',
  STATION: 'ID trạm (bếp / bar) — người dùng gắn với trạm sẽ nhận.',
};

function SendNotificationDialog({ onClose }: { readonly onClose: () => void }): React.JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [audienceType, setAudienceType] = useState<NotificationAudienceType>('BRANCH');
  const [audienceRef, setAudienceRef] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState<NotificationCategory>('SYSTEM');
  const [priority, setPriority] = useState<NotificationPriority>('NORMAL');

  const send = useMutation({
    mutationFn: () =>
      notificationsApi.send({
        audienceType,
        audienceRef: audienceRef.trim(),
        title: title.trim(),
        body: body.trim() === '' ? null : body.trim(),
        category,
        priority,
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success(
        'Đã gửi thông báo',
        `${result.recipients} người nhận${result.capped ? ' — đã chạm giới hạn 500, hãy thu hẹp đối tượng' : ''}.`,
      );
      onClose();
    },
    onError: (e: unknown) => toast.error('Không gửi được thông báo', errorMessage(e)),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Gửi thông báo"
      description="Thông báo được gửi trong ứng dụng tới tối đa 500 người nhận mỗi lần."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Hủy
          </Button>
          <Button
            loading={send.isPending}
            disabled={title.trim() === '' || audienceRef.trim() === ''}
            onClick={() => send.mutateAsync().catch(() => undefined)}
          >
            Gửi
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FormField label="Đối tượng nhận" required>
          {(f) => (
            <Select
              {...f}
              value={audienceType}
              onChange={(e) => setAudienceType(e.target.value as NotificationAudienceType)}
              options={NOTIFICATION_AUDIENCE_TYPES.map((t) => ({ value: t, label: AUDIENCE_LABEL_VI[t] }))}
            />
          )}
        </FormField>
        <FormField label="Mã đối tượng" required hint={AUDIENCE_REF_HINT_VI[audienceType]}>
          {(f) => <Input {...f} value={audienceRef} onChange={(e) => setAudienceRef(e.target.value)} />}
        </FormField>
        <FormField label="Tiêu đề" required hint="Tối đa 200 ký tự.">
          {(f) => <Input {...f} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />}
        </FormField>
        <FormField label="Nội dung" hint="Tối đa 2000 ký tự. Có thể bỏ trống.">
          {(f) => (
            <Textarea {...f} rows={4} value={body} onChange={(e) => setBody(e.target.value)} maxLength={2000} />
          )}
        </FormField>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Phân loại">
            {(f) => (
              <Select
                {...f}
                value={category}
                onChange={(e) => setCategory(e.target.value as NotificationCategory)}
                options={NOTIFICATION_CATEGORIES.map((cat) => ({ value: cat, label: cat }))}
              />
            )}
          </FormField>
          <FormField label="Mức ưu tiên">
            {(f) => (
              <Select
                {...f}
                value={priority}
                onChange={(e) => setPriority(e.target.value as NotificationPriority)}
                options={NOTIFICATION_PRIORITIES.map((p) => ({ value: p, label: p }))}
              />
            )}
          </FormField>
        </div>
      </div>
    </Modal>
  );
}
